/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-04-17 07:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Page, Card, Button, Link, Icon, TextStyle, Badge } from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Select from 'src/components/views/common/normal-select/Select';

import MainMenu from 'src/components/containers/main-menu';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import history from 'src/routes/history';
import { routes } from 'src/routes';
import { URL } from 'src/constants/links';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';

import * as oauthAction from 'actions/oauth-actions';
import UpgradeConfirmModal from './upgrade-confirm.modal';
import DowngradeConfirmModal from './downgrade-confirm.modal';
import UpgradeSuccessModal from './upgrade-success.modal';
import SwitchFailModal from './switch-fail.modal';
import { getShop } from 'src/helpers';
import { ExternalMinor } from '@shopify/polaris-icons';
import TitleHelp from 'src/components/views/common/title-help';
import turboBannerBg from 'src/assets/images/account-settings/turbo-bg-2x.png';
import turboTextImg from 'src/assets/images/account-settings/turbo-text.svg';

import { APP_LANGUAGES, APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { APP_VERSIONS, APP_VERSION_OLD_VERSION, APP_VERSION_TURBO } from 'src/constants/accounts';
import CrownPremium from 'src/assets/images/common/crown-red.png';

type Props = {
  i18n: any,
  tenantApp: Object,
  handleSubmit: Function,
  pristine: boolean,
  valid: boolean,
  dirty: boolean
};

type State = {
  languageValue: string,
  activeModalUpgradeConfirm: boolean,
  activeModalDowngradeConfirm: boolean,
  activeModalUpgradeSuccess: boolean,
  activeModalSwitchFail: boolean,
  currentAppVersion: string,
  appVersionState: string,
  isDontShowAgainState: boolean
};

type OptionSelect = {
  value: string,
  label: string
};

type ComposedProps = Props & WithI18nProps;

const DISSMISS_POPUP_KEY = 'isDismissPopupSwitchAppConfirm';

class AppPreferences extends Component<ComposedProps, State> {
  constructor(props) {
    super(props);
    const { tenantApp, enableWatermark } = props;
    const { appVersion } = tenantApp;
    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
    const initAppVersion = appVersion
      ? appVersion
      : isEnableShopifyIntegration
      ? APP_VERSION_TURBO
      : APP_VERSION_OLD_VERSION;

    this.state = {
      languageValue: APP_LANGUAGE_ENGLISH_VALUE,
      activeModalUpgradeConfirm: false,
      activeModalDowngradeConfirm: false,
      activeModalUpgradeSuccess: false,
      activeModalSwitchFail: false,
      hideWatermark: !enableWatermark,
      currentAppVersion: initAppVersion,
      isDontShowAgainState: false,
      appVersionState:
        initAppVersion === APP_VERSION_TURBO ? APP_VERSION_OLD_VERSION : APP_VERSION_TURBO
    };
  }

  componentDidMount() {
    const { appLanguage } = this.props;
    try {
      const isDontShowAgain = localStorage.getItem(`${getShop()}-${DISSMISS_POPUP_KEY}`);
      this.setState({ isDontShowAgainState: isDontShowAgain });
    } catch (e) {}

    if (appLanguage) {
      this.setState({
        languageValue: appLanguage
      });
    }
  }

  onSave = data => {
    const { addOptionEmail } = this.props;
    addOptionEmail({
      optEmail: data.optEmail
    });
  };

  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  handleSelectAppLanguage = (option: OptionSelect) => {
    this.setState({
      languageValue: option.value
    });
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  handleWatermark = () => {
    this.setState({
      hideWatermark: !this.state.hideWatermark
    });
    this.props.updateWaterMark(!this.state.hideWatermark);
  };

  handleChangeAppLanguage = () => {
    const { updateCurrentAppLanguage } = this.props;
    const { languageValue } = this.state;
    updateCurrentAppLanguage(languageValue);
  };

  handleChangeAppVersion = appVersionValue => {
    const { updateAppVersion } = this.props;
    updateAppVersion(appVersionValue);
  };

  handleClickUpgradeFromPage = () => {
    const { appVersionState } = this.state;

    try {
      const isDontShowAgain = localStorage.getItem(`${getShop()}-${DISSMISS_POPUP_KEY}`);

      if (isDontShowAgain) {
        this.handleChangeAppVersion(appVersionState);
      } else if (appVersionState === APP_VERSION_TURBO) {
        this.toggleChangeState('activeModalUpgradeConfirm');
      } else if (appVersionState === APP_VERSION_OLD_VERSION) {
        this.toggleChangeState('activeModalDowngradeConfirm');
      }
    } catch (e) {}
  };

  handleClickSwitchingFromModal = checkedDontShowAgain => {
    const { appVersionState } = this.state;

    this.handleChangeAppVersion(appVersionState);

    if (checkedDontShowAgain) {
      try {
        localStorage.setItem(`${getShop()}-${DISSMISS_POPUP_KEY}`, checkedDontShowAgain);
      } catch (err) {}
    }
  };

  onChangeAppVersion = (optionAppVersion: OptionSelect) => {
    this.setState({
      appVersionState: optionAppVersion.value
    });
  };

  gotoShopifyIntegration = () => {
    history.push(routes.shopifyIntegration.view());
  };

  handleToTurboLandingPage = () => {
    history.push(routes.landing.turbo());
  };

  componentWillReceiveProps(nextProps) {
    const { loadingChangeAppVersion, changeAppVersionSuccess } = nextProps;
    const { appVersionState } = this.state;

    if (
      this.props.loadingChangeAppVersion &&
      !loadingChangeAppVersion &&
      appVersionState === APP_VERSION_OLD_VERSION
    ) {
      this.setState({
        activeModalDowngradeConfirm: false
      });
    }

    if (this.props.loadingChangeAppVersion && !loadingChangeAppVersion && changeAppVersionSuccess) {
      try {
        const isDontShowAgain = localStorage.getItem(`${getShop()}-${DISSMISS_POPUP_KEY}`);
        this.setState({ isDontShowAgainState: isDontShowAgain });
      } catch (e) {}

      this.setState({
        appVersionState:
          this.state.appVersionState === APP_VERSION_OLD_VERSION
            ? APP_VERSION_TURBO
            : APP_VERSION_OLD_VERSION,
        currentAppVersion: this.state.appVersionState
      });
    }

    if (
      this.props.loadingChangeAppVersion &&
      !loadingChangeAppVersion &&
      appVersionState === APP_VERSION_TURBO &&
      changeAppVersionSuccess
    ) {
      this.setState({
        activeModalUpgradeConfirm: false,
        activeModalUpgradeSuccess: true,
        currentAppVersion: APP_VERSION_TURBO
      });
    }

    if (
      this.props.loadingChangeAppVersion &&
      !loadingChangeAppVersion &&
      !changeAppVersionSuccess
    ) {
      this.setState({
        activeModalUpgradeConfirm: false,
        activeModalUpgradeSuccess: false,
        activeModalDowngradeConfirm: false,
        activeModalSwitchFail: true
      });
    }
  }

  render() {
    const {
      i18n,
      dirty,
      enableAppVersionSwitching,
      selectedPlan,
      enabledSubscriptionV4,
      loadingChangeLanguage,
      loadingChangeAppVersion
    } = this.props;
    const {
      languageValue,
      activeModalUpgradeConfirm,
      activeModalDowngradeConfirm,
      activeModalUpgradeSuccess,
      activeModalSwitchFail,
      appVersionState,
      currentAppVersion,
      hideWatermark,
      isDontShowAgainState
    } = this.state;

    const optionsLanguages = APP_LANGUAGES.map(function (language) {
      return {
        ...language,
        label: i18n.translate(language.label)
      };
    });
    const optionsAppVersions = APP_VERSIONS.map(function (version) {
      return {
        ...version,
        label: i18n.translate(version.label)
      };
    });

    const appVersionStateOption = optionsAppVersions.find(
      version => version.value === appVersionState
    );

    const isFreemium = selectedPlan.includes('freemium');
    return (
      <div className="BC-Page BC-Page__AccountDetail BC-Page__AppPreferences">
        <MainMenu
          menuId="menu-account-app-preferences"
          menuIdMetaMatchOldNav="menu-general-settings___"
        />
        <Page title={i18n.translate('AdminHTML.AppPreferences.title')}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Tool_LanguageSetting_ManualGuide']} />
          <div className="account-wrapper app-preferences-list">
            <div className="item">
              <div className="left">
                <div className="title">
                  {i18n.translate('AdminHTML.AppPreferences.appLanguage')}
                </div>
                <div className="description">
                  <p>{i18n.translate('AdminHTML.AppPreferences.appLanguage_detail')}</p>
                  <p>{i18n.translate('AdminHTML.AppPreferences.appLanguage_detail2')}</p>
                </div>
              </div>
              <div className="right">
                <Card>
                  <div className="item-preferences">
                    <div className="label">
                      {i18n.translate('AdminHTML.AppPreferences.appLanguage_label')}
                    </div>
                    <div className="content" style={{ display: 'flex', gap: '10px' }}>
                      <div className="option" style={{ flex: 1 }}>
                        <Select
                          options={optionsLanguages}
                          onChange={this.handleSelectAppLanguage}
                          value={optionsLanguages.filter(i => i.value === languageValue)}
                        />
                      </div>
                      <Button
                        primary
                        onClick={this.handleChangeAppLanguage}
                        loading={loadingChangeLanguage}>
                        {i18n.translate('AdminHTML.AppPreferences.appLanguage_btn')}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            {enableAppVersionSwitching && (
              <div className="item">
                <div className="left">
                  <div className="title">
                    {i18n.translate('AdminHTML.AppPreferences.appVersion')}
                  </div>
                  <div className="description">
                    <p>{i18n.translate('AdminHTML.AppPreferences.appVersion_detail')}</p>
                    {/* <p>{i18n.translate('AdminHTML.AppPreferences.appVersion_detail2')}</p> */}
                  </div>
                </div>
                <div className="right">
                  <Card>
                    <p>
                      {i18n.translate('AdminHTML.AppPreferences.appVersion_label2', {
                        b: (
                          <b>
                            {currentAppVersion === APP_VERSION_TURBO
                              ? i18n.translate('AdminHTML.AppPreferences.appVersion_label2_b_turbo')
                              : i18n.translate('AdminHTML.AppPreferences.appVersion_label2_b_old')}
                          </b>
                        )
                      })}
                    </p>
                    <p className="guide">
                      {i18n.translate('AdminHTML.AppPreferences.appVersion_label3', {
                        here: (
                          <Link external url={URL['HowToUpgradeTurbo']}>
                            {i18n.translate('AdminHTML.Common.Action.here')}.
                            <Icon source={ExternalMinor} color="interactive" />
                          </Link>
                        )
                      })}
                    </p>
                    <div className="item-preferences">
                      <div className="label">
                        {i18n.translate('AdminHTML.AppPreferences.appVersion_label')}
                      </div>
                      <div className="content" style={{ display: 'flex', gap: '10px' }}>
                        <div className="option" style={{ flex: 1 }}>
                          <Select
                            options={optionsAppVersions}
                            onChange={this.onChangeAppVersion}
                            value={appVersionStateOption}
                          />
                        </div>
                        <Button
                          primary
                          loading={isDontShowAgainState && loadingChangeAppVersion}
                          onClick={this.handleClickUpgradeFromPage}
                          disabled={appVersionStateOption.value === currentAppVersion}>
                          {i18n.translate(appVersionStateOption.btn)}
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            )}
            {/* Watermark */}
            {enabledSubscriptionV4 && (
              <div className="item">
                <div className="left">
                  <div className="title">
                    {i18n.translate('AdminHTML.AppPreferences.waterMark_LabelLeft')}
                  </div>
                  <div className="description">
                    <p>{i18n.translate('AdminHTML.AppPreferences.waterMark_ContentLeft')}</p>
                  </div>
                </div>
                <div className="right watermark-card">
                  <Card>
                    {/* Display product */}

                    <div
                      className={`watermark-status ${
                        isFreemium ? 'watermark-status--disabled' : ''
                      }`}>
                      <label className="BC-Checkbox-Status">
                        <input
                          type="checkbox"
                          disabled={isFreemium}
                          checked={hideWatermark}
                          onChange={value => this.handleWatermark()}
                        />
                        <span />
                      </label>
                      <div>
                        <span>
                          {i18n.translate('AdminHTML.AppPreferences.waterMark_LabelRight', {
                            t: (
                              <TextStyle variation="strong">
                                {i18n.translate('AdminHTML.AppPreferences.waterMark_LabelRight_t')}
                              </TextStyle>
                            )
                          })}
                        </span>
                        {isFreemium && (
                          <Badge status="warning">
                            <img alt="crown" src={CrownPremium} width={11} height={8} />
                            <span>{i18n.translate('AdminHTML.Common.Badge.premium')}</span>
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex' }}>
                      <div style={{ minWidth: '50px' }} />
                      {isFreemium ? (
                        <p className="guide">
                          {i18n.translate('AdminHTML.AppPreferences.waterMark_ContentRight01', {
                            t: (
                              <Link onClick={() => history.push(routes.subscription.package())}>
                                {i18n.translate('AdminHTML.Common.Action.upgradeToUnlock')}
                                <Icon source={ExternalMinor} color="interactive" />
                              </Link>
                            )
                          })}
                        </p>
                      ) : (
                        <p className="guide">
                          {i18n.translate('AdminHTML.AppPreferences.waterMark_ContentRight02')}
                        </p>
                      )}
                    </div>
                  </Card>
                </div>
              </div>
            )}
            {enableAppVersionSwitching && (
              <div
                className="item turbo-banner"
                style={{ backgroundImage: `url(${turboBannerBg})` }}>
                <img src={turboTextImg} alt="" />
                <Button onClick={this.handleToTurboLandingPage}>
                  {i18n.translate('AdminHTML.AppPreferences.TurboBanner.btnSeeWhatNew')}
                </Button>
              </div>
            )}
          </div>
          <UpgradeConfirmModal
            active={activeModalUpgradeConfirm}
            handleCancel={() => this.toggleChangeState('activeModalUpgradeConfirm')}
            handleConfirm={this.handleClickSwitchingFromModal}
            loadingChangeAppVersion={loadingChangeAppVersion}
          />
          <DowngradeConfirmModal
            active={activeModalDowngradeConfirm}
            handleCancel={() => this.toggleChangeState('activeModalDowngradeConfirm')}
            handleConfirm={this.handleClickSwitchingFromModal}
            loadingChangeAppVersion={loadingChangeAppVersion}
          />
          <UpgradeSuccessModal
            active={activeModalUpgradeSuccess}
            handleCancel={() => this.toggleChangeState('activeModalUpgradeSuccess')}
            gotoShopifyIntegration={this.gotoShopifyIntegration}
          />
          <SwitchFailModal
            active={activeModalSwitchFail}
            appVersionState={appVersionState}
            handleCancel={() => this.toggleChangeState('activeModalSwitchFail')}
          />

          {/* This modal is used for when click any link on this page, the route will change */}
          <RouteLeavingGuard
            when={dirty}
            navigate={path => {
              history.push(path);
            }}
            isGotoWithoutShowModalConfirm={false}
            shouldBlockNavigation={dirty}
          />
        </Page>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  return {
    tenantApp,
    selectedPlan,
    enableWatermark: _.get(
      tenantApp,
      'settings.appPreferences.settings.watermark',
      selectedPlan.includes('freemium')
    ),
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    tenantId: _.get(state.oauth.tenantApp, 'tenantId', ''),
    loadingChangeLanguage: _.get(state.oauth, 'loadingChangeLanguage', false),
    loadingChangeAppVersion: _.get(state.oauth, 'loadingChangeAppVersion', false),
    changeAppVersionSuccess: _.get(state.oauth, 'changeAppVersionSuccess'),
    enableAppVersionSwitching
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  updateWaterMark: watermark =>
    dispatch(oauthAction.updateWaterMark({ enable: !watermark }, props.i18n)),
  updateCurrentAppLanguage: appLanguage =>
    dispatch(oauthAction.updateCurrentAppLanguage({ appLanguage }, props.i18n)),
  updateAppVersion: appVersion => dispatch(oauthAction.updateAppVersion({ appVersion }, props.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(AppPreferences));
